import { DefaultButton, Stack, Text, Toggle } from '@fluentui/react';
import { HidableWidgetType } from 'features/Rooms/PersonalDashboard/widgetVisibility';
import IvicosStrings from 'kits/language/stringKit';
import { Calendar, CheckSquare, FilePenLine, FolderKanban, LucideIcon, Mail, Newspaper, Ticket, TicketPlus } from 'lucide-react';
import React from 'react';
import { IDashboardWidgetsState } from './DashboardWidgets.State';

const ICONS_BY_TYPE: Record<HidableWidgetType, LucideIcon> = {
    outlook: Mail,
    calendar: Calendar,
    todos: CheckSquare,
    zammad: Ticket,
    'zammad-ticket': TicketPlus,
    openproject: FolderKanban,
    documenso: FilePenLine,
    colibo: Newspaper
};

const DashboardWidgetsView: React.FC<IDashboardWidgetsState> = (props) => {
    const { visibleRows, draftHiddenWidgets, onToggleWidget, areProvidersLoaded, isMatrixWidgetEnabled, isSaving, isDirty, onSave } = props;

    // Dedicated settings-screen labels — deliberately not the widget-chrome strings
    // (e.g. zammadWidgetTitle / outlookWidgetConnectTitle), which read differently.
    const labelsByType: Record<HidableWidgetType, string> = {
        outlook: IvicosStrings.dashboardWidgetOutlookLabel,
        calendar: IvicosStrings.dashboardWidgetCalendarLabel,
        todos: IvicosStrings.dashboardWidgetTasksLabel,
        zammad: IvicosStrings.dashboardWidgetZammadLabel,
        'zammad-ticket': IvicosStrings.dashboardWidgetZammadTicketLabel,
        openproject: IvicosStrings.dashboardWidgetOpenProjectLabel,
        documenso: IvicosStrings.dashboardWidgetDocumensoLabel,
        colibo: IvicosStrings.dashboardWidgetColiboLabel
    };

    // Render nothing until the campus providers are known, mirroring PersonalDashboard's own
    // `if (enabledProviders === null) return null`. Showing the heading over an empty row list
    // would read as "your campus has no widgets" while the answer is still loading.
    if (!areProvidersLoaded) return null;

    return (
        <Stack aria-label="dashboard-widgets-settings" tokens={{ childrenGap: 12 }} style={{ padding: '5px 5px 5px 0px' }}>
            <Text variant="large" style={{ fontWeight: 600 }}>
                {IvicosStrings.dashboardWidgetsHeading}
            </Text>
            {isMatrixWidgetEnabled && (
                <Text variant="small" style={{ fontStyle: 'italic' }}>
                    {IvicosStrings.dashboardWidgetsMatrixNote}
                </Text>
            )}
            <Text variant="small">{IvicosStrings.dashboardWidgetsHelperText}</Text>
            {visibleRows.map((type) => {
                const Icon = ICONS_BY_TYPE[type];
                const isVisible = !draftHiddenWidgets.includes(type);

                return (
                    <Stack key={type} horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
                        <Icon size={18} />
                        <Text variant="medium" style={{ flexGrow: 1 }}>
                            {labelsByType[type]}
                        </Text>
                        <Toggle
                            // FluentUI's own prop, not the plain HTML attribute: Toggle overwrites
                            // `aria-label` on the rendered switch from this prop, so passing the
                            // hyphenated attribute leaves the control with no accessible name.
                            ariaLabel={labelsByType[type]}
                            checked={isVisible}
                            // Disabled while a save is in flight so a toggle here can never race the
                            // PATCH round-trip — see the isSaving comment on IDashboardWidgetsProps.
                            disabled={isSaving}
                            styles={{ root: { marginBottom: 0 } }}
                            onChange={(event, checked) => onToggleWidget(type, !checked)}
                        />
                    </Stack>
                );
            })}
            {isDirty && (
                <Stack aria-label="dashboard-widgets-actions" horizontal horizontalAlign="end">
                    <DefaultButton
                        iconProps={{ iconName: 'Save' }}
                        text={IvicosStrings.saveActionTitle}
                        ariaLabel="save-widgets-button"
                        onClick={onSave}
                        disabled={isSaving}
                    />
                </Stack>
            )}
        </Stack>
    );
};

export default DashboardWidgetsView;
